import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { fetchProductsByMpns } from '../../api/products'
import type { ProductCard } from '../../types'
import ProductGrid from '../product/ProductGrid'
import { useLocalePath } from '../layout/LanguageRoute'

/**
 * "Direct te bestellen" band (homepage-v2, T3.2 §6.1.5).
 *
 * The plan asks for "only products with stockStatus=in_stock and a price, hide
 * if empty (G13)". Two recorded facts shape how we realise that:
 *   1. Stock is universally `unknown` in this catalog (G13 / [[no-delivery-time-promises]]),
 *      so a hard in_stock gate would hide the band forever.
 *   2. The homepage must lead with PLC families, not the alphabetical small
 *      parts (accessories/cables) that a generic priced query surfaces first
 *      ([[homepage-focus]]).
 *
 * So we drive the band from the same curated, PLC-forward set the homepage has
 * always used: hand-picked B&R modules verified as active, priced, and imaged —
 * i.e. genuinely orderable today. The copy makes no delivery promise (B3). The
 * band still self-hides if none resolve (G13). When real stock data lands,
 * swapping this for a `stock_status: 'in_stock'` query is a one-line change.
 */
const CURATED_ORDERABLE_MPNS: readonly string[] = [
  'X20CP0411', // Compact-S CPU 240 MHz, Ethernet — entry PLC
  'X20CP0484', // Compact-S CPU 667 MHz, POWERLINK + Ethernet — mid-tier
  'X20CP1685', // CPU ATOM 0.8 GHz, TSN + POWERLINK — high-end
  'X20BC0083', // POWERLINK bus controller — station gateway
  'X20DI9371', // 12x digital input, 24 VDC sink — top seller
  'X20DO9322', // 12x digital output, 24 V 0.5 A source — top seller
]

const SLOT_COUNT = 6

export default function InStockBand({ initialData }: { initialData?: ProductCard[] } = {}) {
  const { t } = useTranslation()
  const lp = useLocalePath()

  const { data } = useQuery({
    queryKey: ['home-orderable-products', CURATED_ORDERABLE_MPNS],
    queryFn: () => fetchProductsByMpns(CURATED_ORDERABLE_MPNS),
    // SSR-seeded from pages/home/+data (as initialFeatured) so the grid is in
    // the server HTML on the SSR build; undefined on the client SPA shell.
    initialData,
  })

  const products = (data ?? [])
    .filter(p => p.effective_price != null || p.price_ex_vat != null)
    .slice(0, SLOT_COUNT)

  // G13: no priced/orderable products (incl. while loading) → render nothing,
  // so there is no heading-then-hide flash and no layout shift.
  if (products.length === 0) return null

  return (
    <section className="py-12 border-t border-neutral-100">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
        <div>
          <h2 className="text-[22px] font-bold text-gcg-text tracking-[-0.015em]">
            {t('shop.homeV2.inStock.heading')}
          </h2>
          <p className="text-sm text-neutral-600 mt-1">{t('shop.homeV2.inStock.subtitle')}</p>
        </div>
        <Link
          to={lp('/products')}
          className="text-sm font-medium text-primary hover:underline flex items-center gap-1"
        >
          {t('shop.homeV2.inStock.viewAll')} <span aria-hidden="true">→</span>
        </Link>
      </div>
      <ProductGrid products={products} isLoading={false} />
    </section>
  )
}
